import fetch from 'isomorphic-fetch';
import querystring from 'query-string';

import config, { isFrontOffice } from '../../../config';

export class ServiceError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = 'ServiceError';
  }
}

// slashes
const trimSlashes = (x): string[] => x.replace(/^\/|\/+$/gm, '');
// combineParts
const c = (...parts: string[]): string =>
  parts.map(p => trimSlashes(p)).join('/');

type RequestOptions = {
  headers: Headers;
  baseURL: string;
  query?: any;
  settings?: any;
  postResponse: Function;
  transformResponse: Function;
  handleServiceResponse?: Function;
  ignoreServiceFailure: boolean;
  errorCodes?: Array<any>;
};

export type RO = Partial<RequestOptions>;

export const defaultHeaders = (): Headers => {
  const headers: Headers = new Headers();
  headers.set('accept', 'application/json');
  headers.set('content-type', 'application/json');
  headers.set('apikey', config('apiKey'));
  headers.set('x-trp-requested-with', 'XMLHttpRequest');
  return headers;
};

export const defaultOptions = (): RequestOptions => ({
  headers: defaultHeaders(),
  baseURL: isFrontOffice()
    ? config('internalApiDomain', 'https://api.dev.awstrp.net')
    : config('externalApiDomain', 'https://api.dev.troweprice.com'),
  settings: {
    credentials: 'include',
    crossDomain: true
  },
  postResponse: (resp): Promise<any> => resp,
  transformResponse: (resp): Promise<any> => resp.json(),
  ignoreServiceFailure: false
});

export const errorStatus = (
  resp,
  errorCodes: Array<any> | undefined
): boolean =>
  errorCodes
    ? errorCodes.includes(resp?.status)
    : !(resp.ok || resp.status === 404);

export const createApplicationErrorCodes = [400, 402, 404, 503, 504];

export const handleServiceFailure = async (
  ignoreServiceFailure: boolean,
  location: string,
  response: any,
  payload: any,
  options?: RO
): Promise<any> => {
  // account for custom error handling provided through options
  // TODO [2020-05-25]: update logic for call response to mimic the pattern `const {data, statusCode, statusText} = service.http.get(args)`
  if (options?.handleServiceResponse)
    return options.handleServiceResponse(response);

  // default error message text to response object
  let text = response;
  // attempt to convert response object via it's .text() functionality
  // this will cause error should the response not be a Response Object Type
  // non-response type occurs if the call fails to fetch or results in a non-response error code (i.e. 404, 504, etc.)
  try {
    text = await response.text();
  } catch (err) {
    // set text error message to reflect response fetch failure
    text = `failed to fetch response from ${location}`;
  }

  // combine the api details to create error message
  // log the error within console
  // return empty object, as no data to save to store, and allow user to progress
  if (ignoreServiceFailure) {
    const errorMsg = `Service call of type ${payload.method} to ${location} failed: \n response status ${response.status} - ${text}`;
    // eslint-disable-next-line no-console
    console.error(errorMsg);
    return {};
  }

  // throw service error if api failure shouldn't be ignored; stops users from progressing
  throw new ServiceError(
    `A service call has failed with status: ${
      response.status
    } (location: ${location}, payload: ${JSON.stringify(payload)}) ${text}`
  );
};

export const request = async (
  method: string,
  url: string,
  data?: any,
  options?: RO
): Promise<any> => {
  const {
    headers,
    baseURL,
    query,
    settings,
    postResponse,
    transformResponse,
    ignoreServiceFailure
  } = {
    ...defaultOptions(),
    ...options
  };

  const opts = {
    method,
    headers,
    ...settings
  };

  const payload = data ? { ...opts, body: JSON.stringify(data) } : opts;

  const qString = query ? `?${querystring.stringify(query)}` : '';

  const location = `${baseURL}/${url}${qString}`;

  const resp = await fetch(location, payload).catch(err =>
    handleServiceFailure(ignoreServiceFailure, location, err, payload, options)
  );

  // account for variances in status codes considered 'errors'
  // allows each call to specify codes requiring error handling
  const errorCodes = options?.errorCodes;

  // sometimes 204s/404s are used to indicate missing content
  // and the application should be able to handle these
  // TODO [2021-02-26]: update handleServiceFailure to return status code, text, and any data on error handling
  // TODO [2021-02-26]: update existing logic for all call responses to mimic the pattern `const {data, statusCode, statusText} = service.http.get(args)`
  if (errorStatus(resp, errorCodes))
    return handleServiceFailure(
      ignoreServiceFailure,
      location,
      resp,
      payload,
      options
    );

  const transformedResp = await transformResponse(resp);
  return postResponse(transformedResp);
};

export type Service = {
  extend: (a: string) => Service;
  index: () => Promise<any>;
  view: (id: string) => Promise<any>;
  add: (data: any) => Promise<any>;
  edit: (id: string, data: any) => Promise<any>;
  replace: (id: string, data: any) => Promise<any>;
  del: (id: string) => Promise<any>;
  [x: string]: any;
};

// @todo does trowe support patch method or should we use post
export const get = async (url, opts): Promise<any> =>
  request('GET', url, undefined, opts);
export const post = async (url, data, opts): Promise<any> =>
  request('POST', url, data, opts);
export const put = async (url, data, opts): Promise<any> =>
  request('PUT', url, data, opts);
export const del = async (url, opts): Promise<any> =>
  request('DELETE', url, undefined, opts);
export const patch = async (url, data, opts): Promise<any> =>
  request('PATCH', url, data, opts);
export const postWithoutPayload = async (url, opts): Promise<any> =>
  request('POST', url, undefined, opts);

export const buildService = (base: string, opts?: RO): Service => {
  const e = (o?: RO): RO => ({ ...opts, ...o });

  const http = {
    get: async (url, o?: RO): Promise<any> => get(c(base, url), e(o)),
    post: async (url, data, o?: RO): Promise<any> =>
      post(c(base, url), data, e(o)),
    put: async (url, data, o?: RO): Promise<any> =>
      put(c(base, url), data, e(o)),
    del: async (url, o?: RO): Promise<any> => del(c(base, url), e(o)),
    patch: async (url, data, o?: RO): Promise<any> =>
      patch(c(base, url), data, e(o)),
    postWithoutPayload: async (url, o?: RO): Promise<any> =>
      postWithoutPayload(c(base, url), e(o))
  };

  // @TODO find out why errors are not being propogated to consumer
  return {
    extend: (path, o?): Service => buildService(c(base, path), e(o)),
    index: async (o?): Promise<any> => http.get(`/`, o),
    view: async (id, o?): Promise<any> => http.get(`/${id}`, o),
    add: async (data, o?): Promise<any> => http.post('', data, o),
    edit: async (id, data, o?): Promise<any> => http.patch(`/${id}`, data, o),
    replace: async (id, data, o?): Promise<any> => http.put(`/${id}`, data, o),
    del: async (id, o?): Promise<any> => http.del(`/${id}`, o),
    http
  };
};
